import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type { CategoryMutationPayload, CategoryPageData } from '@/libs/helpers'
import { getCategoryTypeConfig } from '@/libs/helpers'
import type {
  CategoryKind,
  CategoryParams,
  HookParams,
  ProductCategory,
  ServiceCategory,
} from '@/libs/interfaces'
import { categoryApi } from '@/services/api/category'

type CategoryApiStrategy = {
  getById: (payload: CategoryMutationPayload) => Promise<CategoryMutationPayload>
  create: (payload: CategoryMutationPayload) => Promise<CategoryMutationPayload>
  update: (payload: CategoryMutationPayload) => Promise<unknown>
  remove: (payload: CategoryMutationPayload) => Promise<unknown>
}

const CATEGORY_API_STRATEGIES: Record<CategoryKind, CategoryApiStrategy> = {
  service: {
    getById: (payload) => categoryApi.getByIDService(payload),
    create: (payload) => categoryApi.createService(payload),
    update: (payload) => categoryApi.updateService(payload),
    remove: (payload) => categoryApi.deleteService(payload),
  },
  product: {
    getById: (payload) => categoryApi.getByIDProduct(payload),
    create: (payload) => categoryApi.createProduct(payload),
    update: (payload) => categoryApi.updateProduct(payload),
    remove: (payload) => categoryApi.deleteProduct(payload),
  },
}

export function useCategory({ params, enabled }: HookParams<CategoryParams>) {
  return useQuery<ServiceCategory[]>({
    queryKey: QUERY_KEYS.category.list('service', params),
    queryFn: () => categoryApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}

export function useCategoryByType(
  kind: CategoryKind,
  { orgID, id, enabled }: { orgID: string; id?: string; enabled?: boolean },
) {
  const categoryConfig = getCategoryTypeConfig(kind)
  const categoryStrategy = CATEGORY_API_STRATEGIES[kind]

  return useQuery<CategoryPageData>({
    queryKey: QUERY_KEYS.category.id(kind, id || ''),
    queryFn: async () => {
      const category = await categoryStrategy.getById(
        categoryConfig.buildLookupPayload(orgID, id || ''),
      )

      return categoryConfig.toPageData(category)
    },
    enabled: (enabled ?? true) && !!orgID && !!id,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}

export function useCategoryCreate(kind: CategoryKind) {
  const queryClient = useQueryClient()
  const categoryStrategy = CATEGORY_API_STRATEGIES[kind]
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (payload: CategoryMutationPayload) => categoryStrategy.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.category.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useCategoryUpdate(kind: CategoryKind) {
  const queryClient = useQueryClient()
  const categoryStrategy = CATEGORY_API_STRATEGIES[kind]
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (payload: CategoryMutationPayload) => categoryStrategy.update(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.category.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useCategoryDelete(kind: CategoryKind) {
  const queryClient = useQueryClient()
  const categoryStrategy = CATEGORY_API_STRATEGIES[kind]
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (payload: CategoryMutationPayload) => categoryStrategy.remove(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.category.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useCategoryServiceCreate() {
  return useCategoryCreate('service')
}

export function useCategoryProductCreate() {
  return useCategoryCreate('product')
}

export function useCategoryServiceByID({ params, enabled }: HookParams<Partial<ServiceCategory>>) {
  return useQuery<Partial<ServiceCategory>>({
    queryKey: QUERY_KEYS.category.id('service', params.serviceCategoryID || ''),
    queryFn: () => categoryApi.getByIDService(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}

export function useCategoryProductByID({ params, enabled }: HookParams<Partial<ProductCategory>>) {
  return useQuery<Partial<ProductCategory>>({
    queryKey: QUERY_KEYS.category.id('product', params.productCategoryID || ''),
    queryFn: () => categoryApi.getByIDProduct(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}
export function useCategoryProductInfinite({ params, enabled }: HookParams<CategoryParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.category.infinite('product', params),
    queryFn: ({ pageParam }) =>
      categoryApi.listCursorProduct({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}

export function useCategoryServiceInfinite({ params, enabled }: HookParams<CategoryParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.category.infinite('service', params),
    queryFn: ({ pageParam }) =>
      categoryApi.listCursorService({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}

export function useCategoryServiceUpdate() {
  return useCategoryUpdate('service')
}
export function useCategoryProductUpdate() {
  return useCategoryUpdate('product')
}
export function useCategoryServiceDelete() {
  return useCategoryDelete('service')
}
export function useCategoryProductDelete() {
  return useCategoryDelete('product')
}
